/**
 * useProposalStageStats – Proposal stage counts for Home scorecard (KTU-001)
 * Mimics ReviewWorkflow.tsx: listReports, filter to rfp_proposal, group by status.
 * Trace API: GET /reports?project_id=&exclude_report_type=workplan_generation&limit=
 */

import { useQuery } from '@tanstack/react-query';
import { listReports } from '../api/reports';
import type { Report } from '../types/api';

export interface ProposalStageStats {
  totalProposals: number;
  draft: number;
  submitted: number;
  in_review: number;
  approved: number;
  rejected: number;
}

function isRfpProposal(r: Report): boolean {
  const meta = r.meta ?? {};
  return (
    r.report_type === 'rfp_proposal' ||
    (r.report_type === 'custom' &&
      (meta.report_type_override === 'rfp_proposal' || !!meta.rfp_artifact_id))
  );
}

function groupReportsByStatus(reports: Report[]): ProposalStageStats {
  const stats: ProposalStageStats = {
    totalProposals: 0,
    draft: 0,
    submitted: 0,
    in_review: 0,
    approved: 0,
    rejected: 0,
  };
  reports.forEach((r) => {
    if (!isRfpProposal(r)) return;
    stats.totalProposals++;
    const reviewStatus = r.status === 'completed' ? 'draft' : r.status;
    if (reviewStatus === 'draft') stats.draft++;
    else if (reviewStatus === 'submitted') stats.submitted++;
    else if (reviewStatus === 'in_review') stats.in_review++;
    else if (reviewStatus === 'approved') stats.approved++;
    else if (reviewStatus === 'rejected') stats.rejected++;
  });
  return stats;
}

export function useProposalStageStats(projectId: string | null) {
  return useQuery({
    queryKey: ['proposal-stage-stats', projectId],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const reports = await listReports(projectId, {
        exclude_report_type: 'workplan_generation',
        limit: 500,
      }).catch(() => []);
      const list = Array.isArray(reports) ? reports : [];
      return groupReportsByStatus(list);
    },
    enabled: !!projectId,
    staleTime: 60 * 1000,
  });
}
